import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { useOrgStore } from '@/store/orgStore';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Download, Upload, MessageCircle, Mail, Phone, Globe } from 'lucide-react';
import FileAttachments from '@/components/FileAttachments';
import { getWhatsAppLink, getEmailLink, WHATSAPP_TEMPLATES } from '@/utils/chile';

const channelIcons: Record<string, any> = { whatsapp: MessageCircle, email: Mail, llamada: Phone };
const channelLabels: Record<string, string> = { whatsapp: 'WhatsApp', email: 'Email', llamada: 'Llamada' };
const emptyContact = { name: '', email: '', phone: '', whatsapp: '', preferred_channel: 'whatsapp', position: '', company_id: '' };

export default function ContactsPage() {
  const [contacts, setContacts] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [form, setForm] = useState(emptyContact);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const { currentOrgId } = useOrgStore();

  const fetchData = useCallback(async () => {
    if (!currentOrgId) return;
    const params: any = {};
    if (search) params.search = search;
    const [contactsRes, companiesRes] = await Promise.all([
      api.get('/contacts', { params }),
      api.get('/companies'),
    ]);
    setContacts(contactsRes.data);
    setCompanies(companiesRes.data);
  }, [currentOrgId, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getCompany = (id: string) => companies.find(c => c.id === id);
  const openCreate = () => { setEditItem(null); setForm(emptyContact); setDialogOpen(true); };
  const openEdit = (item: any) => {
    setEditItem(item);
    setForm({ name: item.name, email: item.email, phone: item.phone, whatsapp: item.whatsapp || '', preferred_channel: item.preferred_channel || 'whatsapp', position: item.position, company_id: item.company_id });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editItem) { await api.put(`/contacts/${editItem.id}`, form); toast.success('Contacto actualizado'); }
      else { await api.post('/contacts', form); toast.success('Contacto creado'); }
      setDialogOpen(false); fetchData();
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await api.delete(`/contacts/${deleteId}`); toast.success('Contacto eliminado'); setDeleteId(null); fetchData(); }
    catch { toast.error('Error al eliminar'); }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/contacts/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'contactos.csv'); document.body.appendChild(link); link.click(); link.remove();
      toast.success('CSV exportado');
    } catch { toast.error('Error al exportar'); }
  };

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    const formData = new FormData(); formData.append('file', file);
    try { const { data } = await api.post('/contacts/import/csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(`${data.count} contactos importados`); fetchData(); }
    catch { toast.error('Error al importar'); }
    setImporting(false); e.target.value = '';
  };

  return (
    <div data-testid="contacts-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Contactos</h1>
          <p className="text-sm text-[#475569] mt-1">{contacts.length} contactos registrados</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <Button variant="outline" onClick={handleExportCSV} className="flex-1 sm:flex-none min-h-11 border-[#E2E8F0] text-[#475569] font-medium" data-testid="export-contacts-csv"><Download size={16} className="mr-2" /> Exportar</Button>
          <div className="relative flex-1 sm:flex-none">
            <input type="file" accept=".csv" onChange={handleImportCSV} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
            <Button variant="outline" className="w-full sm:w-auto min-h-11 border-[#E2E8F0] text-[#475569] font-medium pointer-events-none" disabled={importing}><Upload size={16} className="mr-2" /> {importing ? 'Importando...' : 'Importar'}</Button>
          </div>
          <Button onClick={openCreate} className="flex-1 sm:flex-none min-h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold" data-testid="create-contact-btn">
            <Plus size={16} className="mr-2" /> Nuevo Contacto
          </Button>
        </div>
      </div>
      <div className="relative mb-4 max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <Input placeholder="Buscar contactos..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 border-[#E2E8F0]" data-testid="contacts-search" />
      </div>
      <div className="hidden lg:block bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left" data-testid="contacts-table">
            <thead><tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Nombre</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Email</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden xl:table-cell">Telefono</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden xl:table-cell">WhatsApp</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden 2xl:table-cell">Cargo</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Empresa</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden xl:table-cell">Canal</th>
              <th className="py-3 px-4 w-12"></th>
            </tr></thead>
            <tbody>
              {contacts.map((c) => {
                const company = getCompany(c.company_id);
                const ChIcon = channelIcons[c.preferred_channel] || Phone;
                return (
                  <tr key={c.id} className="border-b border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors">
                    <td className="py-3 px-4 font-medium text-[#0F172A]">{c.name}</td>
                    <td className="py-3 px-4 text-[#475569]">
                      {c.email && <a href={getEmailLink(c.email, company ? `Propuesta Comercial - ${company.name}` : '')} className="text-[#2563EB] hover:underline">{c.email}</a>}
                    </td>
                    <td className="py-3 px-4 text-[#475569] hidden xl:table-cell">{c.phone}</td>
                    <td className="py-3 px-4 hidden xl:table-cell">
                      {c.whatsapp && (
                        <a href={getWhatsAppLink(c.whatsapp, WHATSAPP_TEMPLATES['Prospecto'])} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#22C55E] hover:text-[#16A34A]" data-testid={`wa-link-${c.id}`}>
                          <MessageCircle size={14} /> <span className="text-xs">{c.whatsapp}</span>
                        </a>
                      )}
                    </td>
                    <td className="py-3 px-4 text-[#475569] hidden 2xl:table-cell">{c.position}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <span className="text-[#0F172A] font-medium text-xs">{company?.name || '-'}</span>
                        {company?.website && (
                          <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:text-[#1D4ED8]" title={company.website}>
                            <Globe size={13} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 hidden xl:table-cell">
                      <Badge variant="outline" className={`text-xs font-medium ${c.preferred_channel === 'whatsapp' ? 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20' : c.preferred_channel === 'email' ? 'bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20' : 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'}`}>
                        <ChIcon size={11} className="mr-1" />{channelLabels[c.preferred_channel] || c.preferred_channel}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {c.whatsapp && <DropdownMenuItem onClick={() => window.open(getWhatsAppLink(c.whatsapp, WHATSAPP_TEMPLATES['Prospecto']), '_blank')}><MessageCircle size={14} className="mr-2 text-[#22C55E]" /> WhatsApp</DropdownMenuItem>}
                          {c.email && <DropdownMenuItem onClick={() => window.open(getEmailLink(c.email, company ? `Propuesta Comercial - ${company.name}` : ''), '_self')}><Mail size={14} className="mr-2 text-[#2563EB]" /> Email</DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => openEdit(c)}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteId(c.id)} className="text-[#EF4444]"><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
              {contacts.length === 0 && <tr><td colSpan={8} className="py-12 text-center text-[#94A3B8]">No hay contactos</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <ul className="lg:hidden space-y-3" data-testid="contacts-card-list">
        {contacts.map((c) => {
          const company = getCompany(c.company_id);
          const ChIcon = channelIcons[c.preferred_channel] || Phone;
          return (
            <li key={c.id} className="rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <span className="font-semibold text-[#0F172A]">{c.name}</span>
                  <p className="text-xs text-[#475569] mt-0.5 truncate">{c.position || 'Sin cargo'}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className={`text-xs font-medium ${c.preferred_channel === 'whatsapp' ? 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20' : c.preferred_channel === 'email' ? 'bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20' : 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'}`}>
                    <ChIcon size={11} className="mr-1" />{channelLabels[c.preferred_channel] || c.preferred_channel}
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-11 w-11"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {c.whatsapp && <DropdownMenuItem onClick={() => window.open(getWhatsAppLink(c.whatsapp, WHATSAPP_TEMPLATES['Prospecto']), '_blank')}><MessageCircle size={14} className="mr-2 text-[#22C55E]" /> WhatsApp</DropdownMenuItem>}
                      {c.email && <DropdownMenuItem onClick={() => window.open(getEmailLink(c.email, company ? `Propuesta Comercial - ${company.name}` : ''), '_self')}><Mail size={14} className="mr-2 text-[#2563EB]" /> Email</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => openEdit(c)}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteId(c.id)} className="text-[#EF4444]"><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-y-1.5 gap-x-3 text-sm">
                <dt className="text-[#64748B]">Email</dt>
                <dd className="text-right text-[#475569] truncate">
                  {c.email ? <a href={getEmailLink(c.email, company ? `Propuesta Comercial - ${company.name}` : '')} className="text-[#2563EB] hover:underline">{c.email}</a> : '-'}
                </dd>
                <dt className="text-[#64748B]">WhatsApp</dt>
                <dd className="text-right">
                  {c.whatsapp ? (
                    <a href={getWhatsAppLink(c.whatsapp, WHATSAPP_TEMPLATES['Prospecto'])} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#22C55E] hover:text-[#16A34A]" data-testid={`wa-link-${c.id}`}>
                      <MessageCircle size={14} /> <span className="text-xs tabular-nums">{c.whatsapp}</span>
                    </a>
                  ) : <span className="text-[#475569]">-</span>}
                </dd>
                <dt className="text-[#64748B]">Empresa</dt>
                <dd className="text-right">
                  <span className="inline-flex items-center gap-1 justify-end">
                    <span className="text-[#0F172A] font-medium text-xs">{company?.name || '-'}</span>
                    {company?.website && (
                      <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:text-[#1D4ED8]" title={company.website}>
                        <Globe size={13} />
                      </a>
                    )}
                  </span>
                </dd>
              </dl>
            </li>
          );
        })}
        {contacts.length === 0 && (
          <li className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8FAFC] p-8 text-center text-sm text-[#94A3B8]">
            No hay contactos
          </li>
        )}
      </ul>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="contact-dialog">
          <DialogHeader><DialogTitle className="font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editItem ? 'Editar Contacto' : 'Nuevo Contacto'}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-[#E2E8F0]" data-testid="contact-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-[#E2E8F0]" data-testid="contact-email-input" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Telefono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+56 9 XXXX XXXX" className="border-[#E2E8F0]" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">WhatsApp</Label>
                <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+56 9 XXXX XXXX" className="border-[#E2E8F0]" data-testid="contact-whatsapp-input" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Canal Preferido</Label>
                <Select value={form.preferred_channel} onValueChange={(v) => setForm({ ...form, preferred_channel: v })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="contact-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="llamada">Llamada</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Cargo</Label>
                <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="border-[#E2E8F0]" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Empresa</Label>
                <Select value={form.company_id || "none"} onValueChange={(v) => setForm({ ...form, company_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="contact-company-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin empresa</SelectItem>
                    {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.website ? ` (${c.website})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
          </div>
          {editItem && <FileAttachments entityType="contact" entityId={editItem.id} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E2E8F0]">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || !form.name} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="contact-save-btn">{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Eliminar Contacto</AlertDialogTitle>
          <AlertDialogDescription>Esta accion no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="contact-confirm-delete">Eliminar</AlertDialogAction>
          </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
